import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ButtonBase,
  Chip
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import ArrowUpwardRounded from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRounded from "@mui/icons-material/ArrowDownwardRounded";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";

import type { Position } from "@/data/mock-positions";
import { summarizePositions } from "@/data/mock-positions";
import type { PositionsPortfolioSummary } from "@/data/mock-positions";
import { formatUSD } from "@/lib/formatters";
import { TickerAvatar } from "@/components/TickerAvatar";
import type { PositionSortKey } from "@/components/PositionsFilterBar";

// ---- Column schema -------------------------------------------------------

/**
 * 11 columns matching Alpaca's mobile Positions view. Order was picked
 * to put the information the user scans for first — Market Value and
 * Today's P/L — closest to the sticky Asset column, while the
 * reference columns (Avg Entry, Cost Basis) sit in the middle.
 */
type ColumnId =
  | "asset"
  | "price"
  | "qty"
  | "side"
  | "marketValue"
  | "avgEntry"
  | "costBasis"
  | "todayPct"
  | "todayDollar"
  | "totalPct"
  | "totalDollar";

interface ColumnSpec {
  id: ColumnId;
  label: string;
  align: "left" | "right";
  /** Fixed pixel width — keeps the sticky first column behaving. */
  width: number;
  /** If set, the header becomes tap-to-sort and routes through `onSort`. */
  sortKey?: PositionSortKey;
}

const COLUMNS: ColumnSpec[] = [
  { id: "asset",        label: "Asset",         align: "left",  width: 200, sortKey: "ticker" },
  { id: "price",        label: "Price",         align: "right", width: 100 },
  { id: "qty",          label: "Qty",           align: "right", width: 96,  sortKey: "qty" },
  { id: "side",         label: "Side",          align: "right", width: 80 },
  { id: "marketValue",  label: "Market Value",  align: "right", width: 130, sortKey: "marketValue" },
  { id: "avgEntry",     label: "Avg Entry",     align: "right", width: 120, sortKey: "avgEntry" },
  { id: "costBasis",    label: "Cost Basis",    align: "right", width: 130, sortKey: "costBasis" },
  { id: "todayPct",     label: "Today %",       align: "right", width: 110, sortKey: "todayPct" },
  { id: "todayDollar",  label: "Today $",       align: "right", width: 120, sortKey: "todayDollar" },
  { id: "totalPct",     label: "Total %",       align: "right", width: 110, sortKey: "totalPct" },
  { id: "totalDollar",  label: "Total $",       align: "right", width: 130, sortKey: "totalDollar" }
];

// ---- Component -----------------------------------------------------------

interface PositionsAdvancedViewProps {
  /** Already filtered + sorted by the caller. */
  positions: Position[];
  /** Current sort key for header highlighting + arrow direction. */
  sortKey?: PositionSortKey;
  sortDir?: "asc" | "desc";
  /** Header tap callback — wired by the parent. */
  onSort?: (key: PositionSortKey) => void;
  /** Route to position detail — wired by the parent. */
  onPositionClick?: (id: string) => void;
}

/**
 * 11-column Alpaca-style positions table. Layout notes:
 *
 *  • First column (`Asset`) sticks during horizontal scroll — the
 *    ticker is the row identifier and needs to stay visible while
 *    the user swipes through P/L columns.
 *  • Portfolio summary row renders above the individual positions
 *    with the same "translucent tint over opaque background" trick
 *    used in BotsAdvancedView (backgroundImage layered on
 *    background.paper) so the sticky cell doesn't bleed the MV
 *    column's digits through as the user scrolls.
 *  • Percentage + dollar P/L cells are sign-coloured green/red.
 *  • The `Side` column renders a small chip — "Long" reads as a
 *    subtle success tint, "Short" as a warning tint. The chip
 *    doubles as visual confirmation of the Long/Short filter.
 *  • Column headers with a `sortKey` are buttons — tapping cycles
 *    the parent's sort state (same direction-flip semantics as the
 *    filter bar's sort chip).
 */
export function PositionsAdvancedView({
  positions,
  sortKey,
  sortDir,
  onSort,
  onPositionClick
}: PositionsAdvancedViewProps) {
  if (positions.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="body2">No positions match your filters.</Typography>
      </Box>
    );
  }

  const summary = summarizePositions(positions);

  return (
    <Box sx={{ pt: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="caption" color="text.secondary">
          {positions.length}{" "}
          {positions.length === 1 ? "position" : "positions"}
          {summary.shortCount > 0 && (
            <> · {summary.longCount} long · {summary.shortCount} short</>
          )}
        </Typography>
      </Stack>

      {/* Edge-to-edge + sticky-header table — mirrors BotsAdvancedView /
       *  OrdersAdvancedView exactly (Sergey 2026-04-27). The `mx: -2`
       *  cancels the page's px: 2 padding so columns can use the full
       *  viewport width. `maxHeight` + overflow-y: auto turns the
       *  wrapper into a real Y scroll container so the sticky <th>s
       *  pin at the top while body rows scroll beneath. No card
       *  border / radius / paper bg — those gave the previous build
       *  a "boxed in" look the other tabs don't have. */}
      <Box
        sx={{
          overflowX: "auto",
          overflowY: "auto",
          maxHeight: "calc(100vh - 200px)",
          mx: -2,
          "&::-webkit-scrollbar": { width: 6, height: 6 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: (t) => alpha(t.palette.text.secondary, 0.3),
            borderRadius: 3
          }
        }}
      >
        <Table
          size="medium"
          sx={{
            minWidth: 1320,
            // Sergey 2026-04-27 — table typography sweep to body1
            // (1rem) so the values match the "Buying Power" baseline
            // anchoring the rest of the page. Mirrors BotsAdvancedView.
            // `whiteSpace: nowrap` on every body td prevents long
            // dollar values like "−$20,031.56" from wrapping mid-row
            // (Sergey 2026-04-27 saw the summary row's Total $ break
            // because of the column min-width).
            "& tbody td": {
              fontSize: "1rem",
              py: 1.5,
              whiteSpace: "nowrap"
            }
          }}
        >
          <TableHead>
            <TableRow>
              {COLUMNS.map((col, i) => (
                <TableCell
                  key={col.id}
                  align={col.align}
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                    // Bump 11 → 12 so the header reads at a comfortable
                    // scale without overpowering the body1 values.
                    // Letter-spacing kept at 0.3 for the uppercase feel.
                    fontSize: 12,
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                    bgcolor: "background.paper",
                    width: col.width,
                    py: 1.25,
                    // Spread first (carries left:0 + z-index:2 + box-
                    // shadow for col 0), then layer the header-row
                    // pinning ON TOP so the values win where they
                    // overlap. Mirrors BotsAdvancedView (Sergey
                    // 2026-04-27 polish parity).
                    ...(i === 0 && stickyFirstCellSx),
                    position: "sticky",
                    top: 0,
                    // Corner cell beats both row-level sticky first
                    // cell (z-index 2) and other header cells when
                    // both axes are scrolling.
                    zIndex: i === 0 ? 3 : 2,
                    borderBottom: (t) => `1px solid ${t.palette.divider}`
                  }}
                >
                  {col.sortKey && onSort ? (
                    <SortableHeader
                      label={col.label}
                      active={sortKey === col.sortKey}
                      dir={sortKey === col.sortKey ? sortDir : undefined}
                      align={col.align}
                      onClick={() => onSort(col.sortKey!)}
                    />
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {positions.length > 1 && <PortfolioSummaryRow summary={summary} />}
            {positions.map((p) => (
              <PositionRow
                key={p.id}
                position={p}
                onClick={
                  onPositionClick ? () => onPositionClick(p.id) : undefined
                }
              />
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

// ---- Sortable header -----------------------------------------------------

function SortableHeader({
  label,
  active,
  dir,
  align,
  onClick
}: {
  label: string;
  active: boolean;
  dir?: "asc" | "desc";
  align: "left" | "right";
  onClick: () => void;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        typography: "inherit",
        color: active ? "primary.main" : "text.secondary",
        fontWeight: 600,
        fontSize: "inherit",
        letterSpacing: "inherit",
        textTransform: "inherit",
        px: 0.5,
        py: 0.25,
        borderRadius: 0.5,
        flexDirection: align === "right" ? "row-reverse" : "row",
        gap: 0.25,
        "&:hover": {
          bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.08),
          color: "primary.main"
        }
      }}
    >
      {active &&
        (dir === "asc" ? (
          <ArrowUpwardRounded sx={{ fontSize: 14 }} />
        ) : (
          <ArrowDownwardRounded sx={{ fontSize: 14 }} />
        ))}
      {label}
    </ButtonBase>
  );
}

// ---- Portfolio summary row -----------------------------------------------

/**
 * "Portfolio" pinned-top row — same convention BotsAdvancedView uses.
 * Tinted bg + bold weight signals the row is an aggregate, not a
 * regular position. Cells that don't aggregate (price, qty, side,
 * avg entry) render as `—`.
 */
function PortfolioSummaryRow({
  summary
}: {
  summary: PositionsPortfolioSummary;
}) {
  return (
    <TableRow
      sx={(t) => ({
        bgcolor: alpha(t.palette.primary.main, 0.06),
        "& td": { fontWeight: 700 }
      })}
    >
      <TableCell
        sx={{
          ...stickyFirstCellSx,
          whiteSpace: "nowrap",
          // Sticky cells need an OPAQUE background or the scrolling
          // columns on the right bleed through the 6% tint. Same
          // trick BotsAdvancedView uses: paint an opaque base
          // (background.paper) and layer the tint on top via
          // backgroundImage so the rendered colour matches the row.
          bgcolor: "background.paper",
          backgroundImage: (t) =>
            `linear-gradient(${alpha(t.palette.primary.main, 0.06)}, ${alpha(
              t.palette.primary.main,
              0.06
            )})`
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box
            sx={(t) => ({
              width: 28,
              height: 28,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              color: t.palette.primary.main
            })}
          >
            <SmartToyOutlined sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Portfolio
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", lineHeight: 1.2 }}
            >
              {summary.count}{" "}
              {summary.count === 1 ? "position" : "positions"}
              {summary.shortCount > 0 && (
                <>
                  {" "}· {summary.longCount}L / {summary.shortCount}S
                </>
              )}
            </Typography>
          </Box>
        </Stack>
      </TableCell>
      {/* price */}
      <TableCell align="right" sx={{ color: "text.secondary" }}>—</TableCell>
      {/* qty */}
      <TableCell align="right" sx={{ color: "text.secondary" }}>—</TableCell>
      {/* side */}
      <TableCell align="right" sx={{ color: "text.secondary" }}>—</TableCell>
      {/* market value */}
      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum" 1' }}>
        {formatUSD(summary.totalMarketValue)}
      </TableCell>
      {/* avg entry */}
      <TableCell align="right" sx={{ color: "text.secondary" }}>—</TableCell>
      {/* cost basis */}
      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum" 1' }}>
        {formatUSD(summary.totalCostBasis)}
      </TableCell>
      {/* today % */}
      <TableCell align="right">
        <SignedPctCell value={summary.weightedTodayPLPct} />
      </TableCell>
      {/* today $ */}
      <TableCell align="right">
        <SignedDollarCell value={summary.totalTodayPL} />
      </TableCell>
      {/* total % */}
      <TableCell align="right">
        <SignedPctCell value={summary.totalUnrealizedPLPct} />
      </TableCell>
      {/* total $ */}
      <TableCell align="right">
        <SignedDollarCell value={summary.totalUnrealizedPL} />
      </TableCell>
    </TableRow>
  );
}

/** Cell-level helpers — render the summary row's signed % / $ cells in
 *  green/red. Triangle prefix (▲/▼) instead of +/− so direction is
 *  conveyed by glyph, matching the Robinhood-style delta lines used
 *  elsewhere on the page (Sergey 2026-04-27). */
function SignedPctCell({ value }: { value: number }) {
  const positive = value > 0;
  const negative = value < 0;
  const triangle = positive ? "▲" : negative ? "▼" : "";
  return (
    <Box
      component="span"
      sx={(t) => ({
        color: positive
          ? t.palette.success.main
          : negative
            ? t.palette.error.main
            : t.palette.text.primary,
        fontFeatureSettings: '"tnum" 1',
        whiteSpace: "nowrap"
      })}
    >
      {triangle && <>{triangle}{" "}</>}
      {Math.abs(value * 100).toFixed(2)}%
    </Box>
  );
}

function SignedDollarCell({ value }: { value: number }) {
  const positive = value > 0;
  const negative = value < 0;
  const triangle = positive ? "▲" : negative ? "▼" : "";
  return (
    <Box
      component="span"
      sx={(t) => ({
        color: positive
          ? t.palette.success.main
          : negative
            ? t.palette.error.main
            : t.palette.text.primary,
        fontFeatureSettings: '"tnum" 1',
        whiteSpace: "nowrap"
      })}
    >
      {triangle && <>{triangle}{" "}</>}
      {formatUSD(Math.abs(value))}
    </Box>
  );
}

// ---- Position row --------------------------------------------------------

function PositionRow({
  position,
  onClick
}: {
  position: Position;
  onClick?: () => void;
}) {
  return (
    <TableRow
      hover
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
        "&:last-of-type td": { borderBottom: 0 }
      }}
    >
      <TableCell sx={{ ...stickyFirstCellSx, bgcolor: "background.paper" }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ flexShrink: 0 }}>
            <TickerAvatar symbol={position.asset} size={28} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.15
                }}
              >
                {position.asset}
              </Typography>
              {position.side === "short" && (
                <Chip
                  label="Short"
                  size="small"
                  sx={(t) => ({
                    height: 15,
                    bgcolor: alpha(t.palette.warning.main, 0.15),
                    color: t.palette.warning.main,
                    fontWeight: 600,
                    "& .MuiChip-label": { px: 0.5, fontSize: 9.5 }
                  })}
                />
              )}
            </Stack>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                lineHeight: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
                maxWidth: 150
              }}
            >
              {position.assetName}
            </Typography>
          </Box>
        </Stack>
      </TableCell>
      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum" 1' }}>
        {formatUSD(position.currentPrice)}
      </TableCell>
      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum" 1' }}>
        {formatQty(position.qty)}
      </TableCell>
      <TableCell align="right">
        <SideChip side={position.side} />
      </TableCell>
      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum" 1' }}>
        {formatUSD(position.marketValue)}
      </TableCell>
      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum" 1' }}>
        {formatUSD(position.avgEntryPrice)}
      </TableCell>
      <TableCell align="right" sx={{ fontFeatureSettings: '"tnum" 1' }}>
        {formatUSD(position.costBasis)}
      </TableCell>
      <TableCell align="right">
        <SignedPct value={position.todayPLPct} />
      </TableCell>
      <TableCell align="right">
        <SignedDollar value={position.todayPLDollar} />
      </TableCell>
      <TableCell align="right">
        <SignedPct value={position.totalPLPct} />
      </TableCell>
      <TableCell align="right">
        <SignedDollar value={position.totalPLDollar} />
      </TableCell>
    </TableRow>
  );
}

// ---- Side chip -----------------------------------------------------------

function SideChip({ side }: { side: Position["side"] }) {
  const isLong = side === "long";
  return (
    <Chip
      label={isLong ? "Long" : "Short"}
      size="small"
      sx={(t) => ({
        height: 18,
        bgcolor: isLong
          ? alpha(t.palette.success.main, 0.14)
          : alpha(t.palette.warning.main, 0.16),
        color: isLong ? t.palette.success.main : t.palette.warning.main,
        fontWeight: 600,
        "& .MuiChip-label": { px: 0.75, fontSize: 10, letterSpacing: 0.2 }
      })}
    />
  );
}

// ---- Sign-coloured cells -------------------------------------------------

function SignedPct({ value }: { value: number }) {
  const positive = value > 0;
  const negative = value < 0;
  const color = (t: Theme) => {
    if (positive) return t.palette.success.main;
    if (negative) return t.palette.error.main;
    return t.palette.text.primary;
  };
  // U+2212 typographic minus — aligns better than the ASCII hyphen
  // in right-aligned tabular-nums columns.
  const triangle = positive ? "\u25b2" : negative ? "\u25bc" : "";
  return (
    <Typography
      variant="body1"
      sx={{
        color,
        fontWeight: 600,
        fontFeatureSettings: '"tnum" 1',
        whiteSpace: "nowrap"
      }}
    >
      {triangle && <>{triangle}{" "}</>}
      {Math.abs(value * 100).toFixed(2)}%
    </Typography>
  );
}

function SignedDollar({ value }: { value: number }) {
  const positive = value > 0;
  const negative = value < 0;
  const color = (t: Theme) =>
    positive
      ? t.palette.success.main
      : negative
        ? t.palette.error.main
        : t.palette.text.primary;
  const triangle = positive ? "\u25b2" : negative ? "\u25bc" : "";
  return (
    <Typography
      variant="body1"
      sx={{
        color,
        fontWeight: 600,
        fontFeatureSettings: '"tnum" 1',
        whiteSpace: "nowrap"
      }}
    >
      {triangle && <>{triangle}{" "}</>}
      {formatUSD(Math.abs(value))}
    </Typography>
  );
}

// ---- Sticky first-column sx ---------------------------------------------

const stickyFirstCellSx = {
  position: "sticky" as const,
  left: 0,
  zIndex: 2,
  boxShadow: (t: Theme) => `inset -1px 0 0 0 ${t.palette.divider}`
};

// ---- Format helpers ------------------------------------------------------

/**
 * Quantity display — integer for whole shares, up to 4 decimals for
 * fractional crypto/ETF positions. Keeps the column tight while still
 * showing enough precision for sub-$1 crypto.
 */
function formatQty(qty: number): string {
  if (Number.isInteger(qty)) {
    return qty.toLocaleString();
  }
  if (qty >= 100) {
    return qty.toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });
  }
  if (qty >= 1) {
    return qty.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }
  return qty.toLocaleString(undefined, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4
  });
}
